import { Button } from 'components/atoms';
import { InfoBox, MagellanPopups } from 'components/molecules';
import { TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiYoutubeLogo } from 'react-icons/pi';
import { useGetYouTubeContentIdAuthorizationStatuses } from 'services/enums';
import {
  useAuthorizeYoutubeAdmin,
  useGetYoutubeAuthorizationSettings,
} from 'services/youtube';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

const YoutubeOAuth = () => {
  const { t } = useTranslation();
  const i18nextPrefix = 'youtubeOAuth';

  const authorizationSettings = useGetYoutubeAuthorizationSettings();
  const authorizationStatuses = useGetYouTubeContentIdAuthorizationStatuses();
  const authorizeAdmin = useAuthorizeYoutubeAdmin();

  const statusLabel = useMemo(() => {
    const statusId = authorizationSettings.data?.authorizationStatusId;

    if (statusId === undefined) {
      return undefined;
    }

    return (
      authorizationStatuses.data?.find(status => status.id === statusId)
        ?.name ?? String(statusId)
    );
  }, [
    authorizationSettings.data?.authorizationStatusId,
    authorizationStatuses.data,
  ]);

  const hasRefreshTokenLabel =
    authorizationSettings.data?.hasRefreshToken === undefined
      ? undefined
      : authorizationSettings.data.hasRefreshToken
        ? t('common.yes')
        : t('common.no');

  const isLoading =
    authorizationSettings.isLoading || authorizationStatuses.isLoading;

  const canAuthorizeMagelup =
    authorizationSettings.data?.hasRefreshToken === true;

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          customBackItem={<div />}
          topbarContent={
            <>
              <Button
                variant={BUTTON_VARIANT.submit}
                isLoading={authorizeAdmin.isPending}
                onClick={() => authorizeAdmin.mutate()}>
                <Button.Text text={t(`${i18nextPrefix}.authorizeAdmin`)} />
                <Button.Icon icon={<PiYoutubeLogo />} />
              </Button>
              <MagellanPopups.YoutubeMagelupAuthorize
                adminAuthorized={canAuthorizeMagelup}
                onSuccess={() => authorizationSettings.refetch()}
                trigger={
                  <Button
                    variant={BUTTON_VARIANT.default}
                    disabled={!canAuthorizeMagelup || isLoading}
                    >
                    <Button.Text
                      text={t(`${i18nextPrefix}.authorizeMagelup`)}
                    />
                    <Button.Icon icon={<PiYoutubeLogo />} />
                  </Button>
                }
              />
            </>
          }>
          <InfoBox loading={isLoading}>
            <InfoBox.Items columns={2} rows={1}>
              <InfoBox.Item
                label={t(`${i18nextPrefix}.status.authorizationStatus`)}
                value={statusLabel}
              />
              <InfoBox.Item
                label={t(`${i18nextPrefix}.status.hasRefreshToken`)}
                value={hasRefreshTokenLabel}
              />
            </InfoBox.Items>
          </InfoBox>
        </TopBar>
      }
    />
  );
};

export default YoutubeOAuth;
